import { useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'

export interface InsightSourceLink {
  date: string
  language: string
  url: string
}

export interface InsightSource {
  links?: InsightSourceLink[]
  platform: string
  text: string
}

export type CampaignCategory =
  | 'Brand Management'
  | 'Competitor Analysis'
  | 'Market Research'

// Which feature module an insight originates from. Drives the Home "Source"
// filter. Matches the backend `module` tag (projectSmartInsights/collectors).
export type InsightModule =
  | 'social-listening'
  | 'ai-listening'
  | 'inbox-analysis'
  | 'content-analysis'
  | 'review-analysis'
  | 'ad-analysis'

// The window an insight describes. Resolved server-side per insight, since no
// module stores it uniformly: stored `period` (review-analysis) → earliest/
// latest source link date (inbox, social, content) → `checkedAt` (ai-listening)
// → `createdAt`. This is what fromDate/toDate filter against.
export interface InsightPeriod {
  from: string
  to: string
}

export interface SmartInsight {
  aiRecommendation: string
  campaignCategories?: CampaignCategory[]
  category: string
  // When the insight was generated — distinct from `period`, the window it covers.
  createdAt?: null | string
  details?: string
  id: string
  isAIRecommendation?: boolean
  isSmartInsight?: boolean
  module?: InsightModule
  percentageChange?: number
  period?: InsightPeriod | null
  sources?: InsightSource[]
  title: string
  type: 'challenge' | 'strength'
}

export interface SmartInsightsResponse {
  hasInsights: boolean
  insights?: SmartInsight[]
}

interface UseGetSmartInsightsParams {
  campaign?: string
  fromDate?: string
  // Scope to one project's linked entities across all modules (Home Project
  // filter). Ignored by the backend when `campaign` is also set.
  projectId?: string
  toDate?: string
}

export const useGetSmartInsightsQuery = (
  params?: UseGetSmartInsightsParams,
  options?: { enabled?: boolean }
) => {
  return useQuery<SmartInsightsResponse>({
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const response = await analyticsApi().get('/v1_private/smart-insights', {
        params: {
          campaign: params?.campaign,
          fromDate: params?.fromDate,
          projectId: params?.projectId,
          toDate: params?.toDate,
        },
      })
      return response.data
    },
    queryKey: [
      QueryKey.GET_SMART_INSIGHTS,
      params?.campaign,
      params?.projectId,
      params?.fromDate,
      params?.toDate,
    ],
    staleTime: 5 * 60 * 1000, // 5 minutes — reuse cached data instead of refetching on every mount
  })
}
